package com.karyadina.service

import com.karyadina.model.Task
import com.karyadina.model.TaskCategory
import com.karyadina.model.TaskPriority
import com.karyadina.model.TaskRecurrence
import com.karyadina.model.TaskStats
import com.karyadina.model.TaskStatus
import kotlinx.datetime.*
import kotlinx.coroutines.*
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

interface TaskService {
    fun createTask(task: Task): Task
    fun updateTask(task: Task): Task
    fun deleteTask(taskId: String)
    fun getTask(taskId: String): Task?
    fun getAllTasks(): List<Task>
    fun getTasksByStatus(status: TaskStatus): List<Task>
    fun getTasksByPriority(priority: TaskPriority): List<Task>
    fun getTasksByCategory(category: TaskCategory): List<Task>
    fun getOverdueTasks(): List<Task>
    fun getTodayTasks(): List<Task>
    fun getActiveTasks(): List<Task>
    fun getUpcomingTasks(): List<Task>
    fun completeTask(taskId: String): Task
    fun getStatistics(): TaskStats
    fun searchTasks(query: String): List<Task>
    fun getTasksCompletedSince(timestamp: Long): List<Task>
    fun getIncompleteTasksBefore(timestamp: Long): List<Task>
    /** Re-read all data from persistent storage (e.g. after cloud sync import) */
    fun refreshFromStorage()
}

/**
 * Implementation of TaskService with persistent storage
 */
class TaskServiceImpl(
    private val storage: TaskStorage
) : TaskService {
    private val tasks: MutableList<Task>
    
    init {
        tasks = storage.loadTasks().toMutableList()
        
        // Listen for storage refresh signals (e.g. from Cloud Sync)
        // Since this is a singleton service, we use a background scope to keep it alive
        kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Default).launch {
            AppStorage.refreshNotifier.collect {
                refreshFromStorage()
            }
        }
    }

    override fun refreshFromStorage() {
        tasks.clear()
        tasks.addAll(storage.loadTasks())
    }
    
    private fun saveTasks() {
        storage.saveTasks(tasks)
    }
    
    @OptIn(ExperimentalTime::class)
    override fun createTask(task: Task): Task {
        val newTask = task.copy(
            id = generateId(),
            createdAt = Clock.System.now().toEpochMilliseconds(),
            updatedAt = Clock.System.now().toEpochMilliseconds()
        )
        tasks.add(0, newTask)
        saveTasks()
        return newTask
    }
    
    override fun updateTask(task: Task): Task {
        val index = tasks.indexOfFirst { it.id == task.id }
        if (index != -1) {
            val updated = task.copy(updatedAt = Clock.System.now().toEpochMilliseconds())
            tasks[index] = updated
            saveTasks()
            return updated
        }
        throw IllegalArgumentException("Task not found: ${task.id}")
    }
    
    override fun deleteTask(taskId: String) {
        tasks.removeAll { it.id == taskId }
        saveTasks()
    }
    
    override fun getTask(taskId: String): Task? {
        return tasks.find { it.id == taskId }
    }
    
    override fun getAllTasks(): List<Task> {
        return tasks.sortedByDescending { it.createdAt }
    }
    
    override fun getTasksByStatus(status: TaskStatus): List<Task> {
        return tasks.filter { it.status == status.name }.sortedByDescending { it.createdAt }
    }
    
    override fun getTasksByPriority(priority: TaskPriority): List<Task> {
        return tasks.filter { it.priority == priority.name }.sortedByDescending { it.createdAt }
    }
    
    override fun getTasksByCategory(category: TaskCategory): List<Task> {
        return tasks.filter { it.category == category.name }.sortedByDescending { it.createdAt }
    }
    
    @OptIn(ExperimentalTime::class)
    override fun getOverdueTasks(): List<Task> {
        val now = Clock.System.now().toEpochMilliseconds()
        return tasks.filter { 
            it.status != TaskStatus.COMPLETED.name && 
            it.deadline != null && 
            it.deadline!! < now 
        }.sortedBy { it.deadline }
    }

    override fun getActiveTasks(): List<Task> {
        val timeZone = TimeZone.currentSystemDefault()
        val tomorrowStart = Clock.System.now().toLocalDateTime(timeZone).date
            .plus(DatePeriod(days = 1))
            .atStartOfDayIn(timeZone)
            .toEpochMilliseconds()
            
        return tasks.filter { 
            it.status != TaskStatus.COMPLETED.name && (
                it.deadline == null || it.deadline!! < tomorrowStart
            )
        }.sortedBy { it.deadline ?: Long.MAX_VALUE }
    }
    
    @OptIn(ExperimentalTime::class)
    override fun getTodayTasks(): List<Task> {
        val timeZone = TimeZone.currentSystemDefault()
        val today = Clock.System.now().toLocalDateTime(timeZone).date
        val todayEnd = today.plus(DatePeriod(days = 1)).atStartOfDayIn(timeZone).toEpochMilliseconds()
        
        return tasks.filter { 
            it.status != TaskStatus.COMPLETED.name && (
                // Tasks with today's deadline or earlier (overdue are included here)
                it.deadline == null || it.deadline!! < todayEnd
            )
        }.sortedBy { it.deadline ?: Long.MAX_VALUE }
    }
    
    @OptIn(ExperimentalTime::class)
    override fun getUpcomingTasks(): List<Task> {
        val timeZone = TimeZone.currentSystemDefault()
        val now = Clock.System.now()
        val today = now.toLocalDateTime(timeZone).date
        
        // Start from tomorrow
        val tomorrowStart = today.plus(DatePeriod(days = 1)).atStartOfDayIn(timeZone).toEpochMilliseconds()
        // Up to 7 days from now
        val nextWeekEnd = today.plus(DatePeriod(days = 7)).atStartOfDayIn(timeZone).toEpochMilliseconds()
        
        return tasks.filter { 
            it.status != TaskStatus.COMPLETED.name &&
            it.deadline != null && 
            it.deadline!! >= tomorrowStart && 
            it.deadline!! < nextWeekEnd 
        }.sortedBy { it.deadline }
    }
    
    @OptIn(ExperimentalTime::class)
    override fun completeTask(taskId: String): Task {
        val task = getTask(taskId) ?: throw IllegalArgumentException("Task not found: $taskId")
        val completedTask = task.copy(
            status = TaskStatus.COMPLETED.name,
            completedAt = Clock.System.now().toEpochMilliseconds()
        )
        val result = updateTask(completedTask)
        
        // Handle recurrence
        if (task.recurrence != TaskRecurrence.NONE.name) {
            val recurrence = try {
                TaskRecurrence.valueOf(task.recurrence)
            } catch (e: Exception) { TaskRecurrence.NONE }
            
            if (recurrence != TaskRecurrence.NONE) {
                createRecurringTask(task, recurrence)
            }
        }
        
        return result
    }
    
    @OptIn(ExperimentalTime::class)
    private fun createRecurringTask(originalTask: Task, recurrence: TaskRecurrence) {
        val timeZone = TimeZone.currentSystemDefault()
        
        // Calculate new deadline
        // If has deadline, add interval to it. 
        // If no deadline, maybe add interval to Today? Usually recurring tasks have deadlines.
        // If no deadline, we can't really recur "on schedule". Let's assume deadline exists or fallback to created+interval.
        
        val baseTime = originalTask.deadline ?: originalTask.createdAt
        val instant = Instant.fromEpochMilliseconds(baseTime)
        val localDateTime = instant.toLocalDateTime(timeZone)
        
        val newLocalDateTime = when (recurrence) {
            TaskRecurrence.DAILY -> localDateTime.date.plus(DatePeriod(days = 1)).atTime(localDateTime.time)
            TaskRecurrence.WEEKLY -> localDateTime.date.plus(DatePeriod(days = 7)).atTime(localDateTime.time)
            TaskRecurrence.MONTHLY -> localDateTime.date.plus(DatePeriod(months = 1)).atTime(localDateTime.time)
            else -> localDateTime
        }
        
        val newDeadline = newLocalDateTime.toInstant(timeZone).toEpochMilliseconds()
        
        val newTask = originalTask.copy(
            id = generateId(), // New ID
            status = TaskStatus.TODO.name,
            deadline = newDeadline,
            createdAt = Clock.System.now().toEpochMilliseconds(),
            updatedAt = Clock.System.now().toEpochMilliseconds(),
            completedAt = null,
            // Keep recurrence set so it keeps repeating!
            recurrence = recurrence.name
        )
        
        tasks.add(0, newTask)
        saveTasks()
    }
    
    override fun getStatistics(): TaskStats {
        val total = tasks.size
        val completed = tasks.count { it.status == TaskStatus.COMPLETED.name }
        val overdue = getOverdueTasks().size
        val today = getTodayTasks().size
        val week = getUpcomingTasks().size
        
        val completionRate = if (total > 0) (completed.toFloat() / total * 100) else 0f
        
        val completedTasks = tasks.filter { 
            it.status == TaskStatus.COMPLETED.name && 
            it.completedAt != null && 
            it.actualMinutes != null 
        }
        val avgTime = if (completedTasks.isNotEmpty()) {
            completedTasks.sumOf { it.actualMinutes ?: 0 } / completedTasks.size
        } else 0
        
        return TaskStats(
            totalTasks = total,
            completedTasks = completed,
            overdueTasks = overdue,
            todayTasks = today,
            weekTasks = week,
            completionRate = completionRate,
            averageCompletionTime = avgTime
        )
    }
    
    override fun searchTasks(query: String): List<Task> {
        val lowerQuery = query.lowercase()
        return tasks.filter {
            it.title.lowercase().contains(lowerQuery) ||
            it.description.lowercase().contains(lowerQuery) ||
            it.tags.any { tag -> tag.lowercase().contains(lowerQuery) } ||
            it.subject?.lowercase()?.contains(lowerQuery) == true
        }.sortedByDescending { it.createdAt }
    }

    override fun getTasksCompletedSince(timestamp: Long): List<Task> {
        return tasks.filter { 
            it.status == TaskStatus.COMPLETED.name && 
            it.completedAt != null && 
            it.completedAt!! >= timestamp 
        }.sortedByDescending { it.completedAt }
    }

    @OptIn(ExperimentalTime::class)
    override fun getIncompleteTasksBefore(timestamp: Long): List<Task> {
        return tasks.filter {
            it.status != TaskStatus.COMPLETED.name &&
            it.deadline != null &&
            it.deadline!! < timestamp
        }.sortedBy { it.deadline }
    }
    
    @OptIn(ExperimentalTime::class)
    private fun generateId(): String {
        return "task_${Clock.System.now().toEpochMilliseconds()}_${(1000..9999).random()}"
    }
}
